import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type LoanStatus = "active" | "completed";

export type EmployeeLoanRecord = {
  id: string;
  employeeId: string;
  loanAmount: string;
  installmentAmount: string;
  totalInstallments: string;
  remainingAmount: string;
  startDate: string;
  status: LoanStatus;
  createdAt: string;
  updatedAt: string;
};

export type EmployeeLoanInstallmentRecord = {
  id: string;
  loanId: string;
  salaryId: string | null;
  periodYear: string;
  periodMonth: string;
  installmentAmount: string;
  deductedOn: string | null;
  status: "paid" | "pending" | "unpaid";
  note: string | null;
  createdAt: string;
  updatedAt: string;
};

type PaginatedResponse = {
  data?: Record<string, unknown>[];
  meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number };
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapLoan(raw: Record<string, unknown>): EmployeeLoanRecord {
  const status = str(raw.status) === "completed" ? "completed" : "active";
  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    loanAmount: str(raw.loan_amount),
    installmentAmount: str(raw.installment_amount),
    totalInstallments: str(raw.total_installments),
    remainingAmount: str(raw.remaining_amount),
    startDate: str(raw.start_date).slice(0, 10),
    status,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

function mapInstallment(raw: Record<string, unknown>): EmployeeLoanInstallmentRecord {
  const rawStatus = str(raw.status);
  const status: EmployeeLoanInstallmentRecord["status"] =
    rawStatus === "unpaid" ? "unpaid" : rawStatus === "pending" ? "pending" : "paid";
  return {
    id: str(raw.id),
    loanId: str(raw.loan_id),
    salaryId: raw.salary_id != null ? str(raw.salary_id) : null,
    periodYear: str(raw.period_year),
    periodMonth: str(raw.period_month),
    installmentAmount: str(raw.installment_amount),
    deductedOn: raw.deducted_on != null ? str(raw.deducted_on).slice(0, 10) : null,
    status,
    note: raw.note != null ? str(raw.note) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

export function loansApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listEmployeeLoans(params: {
  employeeId: string;
  page?: number;
  perPage?: number;
  status?: "all" | LoanStatus;
}): Promise<{ rows: EmployeeLoanRecord[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>(`/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans`, {
    params: {
      page: params.page,
      per_page: params.perPage,
      status: params.status && params.status !== "all" ? params.status : undefined,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapLoan(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function createEmployeeLoan(params: {
  employeeId: string;
  loanAmount: number | string;
  installmentAmount: number | string;
  startDate: string;
}): Promise<{ record: EmployeeLoanRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans`,
    {
      loan_amount: params.loanAmount,
      installment_amount: params.installmentAmount,
      start_date: params.startDate,
    },
  );
  return { record: mapLoan((response.data?.data ?? {}) as Record<string, unknown>), message: str(response.data?.message) || "Created." };
}

export async function updateEmployeeLoan(params: {
  employeeId: string;
  id: string;
  patch: Partial<{ installmentAmount: number | string; startDate: string; status: LoanStatus }>;
}): Promise<{ record: EmployeeLoanRecord; message: string }> {
  const body: Record<string, unknown> = {};
  if (params.patch.installmentAmount !== undefined) body.installment_amount = params.patch.installmentAmount;
  if (params.patch.startDate !== undefined) body.start_date = params.patch.startDate;
  if (params.patch.status !== undefined) body.status = params.patch.status;

  const response = await api.patch<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.id)}`,
    body,
  );
  return { record: mapLoan((response.data?.data ?? {}) as Record<string, unknown>), message: str(response.data?.message) || "Saved." };
}

export async function closeEmployeeLoan(params: { employeeId: string; id: string }): Promise<{ record: EmployeeLoanRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.id)}/close`,
  );
  return { record: mapLoan((response.data?.data ?? {}) as Record<string, unknown>), message: str(response.data?.message) || "Closed." };
}

export async function listLoanInstallments(params: {
  employeeId: string;
  loanId: string;
  page?: number;
  perPage?: number;
}): Promise<{ rows: EmployeeLoanInstallmentRecord[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.loanId)}/installments`,
    { params: { page: params.page, per_page: params.perPage } },
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapInstallment(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function skipLoanInstallment(params: {
  employeeId: string;
  loanId: string;
  periodYear: number;
  periodMonth: number;
  note?: string | null;
}): Promise<{ record: EmployeeLoanInstallmentRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.loanId)}/skip-installment`,
    {
      period_year: params.periodYear,
      period_month: params.periodMonth,
      note: params.note?.trim() || null,
    },
  );
  return {
    record: mapInstallment((response.data?.data ?? {}) as Record<string, unknown>),
    message: str(response.data?.message) || "Saved.",
  };
}

